import {
    Button,
    CircularProgress,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TablePagination,
    TableRow,
} from "@mui/material";
import axios from "../../../utils/axiosInstance";
import { useSnackbar } from "notistack";
import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router";
import CreateContentVersionPopup from "./createContentVersion";
import DeleteModal from "../../UI/atoms/Popup/DeleteModal";
import { MILLIS_TO_LOCALE } from "../../../utilities/helpers/formatUnix";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import ArticleIcon from "@mui/icons-material/Article";
import {
    DRAFT_STATUS,
    LIVE,
    SCHEDULED_STATUS,
    UNIQUE_NAME_ERROR,
} from "../LandingPage/constants";
import ContentCurationVersionTable from "./ContentCurationVersionsTable";
import {
    ACTION,
    PERMISSIONS,
    usePermissions,
} from "../../../utils/permissions";

const ContentCurationVersions = () => {
    const data = [
        {
            versionName: "test-version-local-10211",
            template: "home_page",
            lastModified: 1687535261583,
            id: "566947f3-e996-466f-8c2f-361cbfe0b021",
            publishStatus: "draft",
        },
    ];
    // hooks
    const navigate = useNavigate();
    const [loader, setLoader] = useState(false);
    const [versionPopup, setVersionPopup] = useState(false);
    const { enqueueSnackbar } = useSnackbar();
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [selectedPage, setSelectedPage] = useState<any>(null);
    const [liveVersionList, setLiveVersionList] = useState<any>([]);
    const [draftVersionList, setDraftVersionList] = useState<any>([]);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [totalCount, setTotalCount] = useState(0);
    const [skipKey, setSkipKey] = useState("");

    const [skipKeyQueue, setskipKeyQueue] = useState<any>("");
    const [masterInformation, setMasterInformation] = useState<any>({});
    const draftTableRef = useRef<any>(null);
    const scheduleTableRef = useRef<any>(null);

    const createVersionPopupRef = useRef<any>(null);
    const { hasPermission } = usePermissions();

    useEffect(() => {
        getLivePages();
        getOtherPages();
        getMasterInfo();
    }, []);

    const resetTableMeta = () => {
        setskipKeyQueue([]);
        setPage(0);
    };

    // call API for getting the master information
    const getMasterInfo = async () => {
        try {
            const materInformation = await axios.get(
                `${process.env.BASE_URL}api/v1/cms/page/master`
            );
            if (materInformation.data?.success) {
                let masterKeyValue: any = {};
                const pageTemplate = materInformation.data?.data?.pageTemplate;
                pageTemplate.map(
                    (template: any) =>
                        (masterKeyValue[template.key] = template.label)
                );
                setMasterInformation(masterKeyValue);
            }
        } catch (error) {
            console.log("Error in getting master information", error);
        }
    };

    const getLivePages = async () => {
        const pages = await getPagesList(LIVE, 10);
        setLiveVersionList(pages.list);
    };

    const getOtherPages = async () => {
        resetTableMeta();
        const pages = await getPagesDraft(DRAFT_STATUS, rowsPerPage);
        setTotalCount(pages.totalCount);
        setDraftVersionList([...pages.list]);
    };

    const getPagesDraft = async (
        status: string = DRAFT_STATUS || undefined,
        limit: number | string = rowsPerPage || 0,
        skip: string = skipKey || "",
        skipQueue: any[] | null = skipKeyQueue
    ) => {
        const pages = await getPagesList(status, limit, skip);
        const list = pages?.list;
        list.length && pushKey(list[list.length - 1]?.id, skipQueue);
        return pages;
    };

    const getPagesList = async (
        status: string,
        limit: number | string,
        skip: string = ""
    ) => {
        try {
            setLoader(true);
            const saveVersion = await axios.get(
                `${process.env.BASE_URL}api/v1/cms/page/filter?status=${status}&skip=${skip}&limit=${limit}`
            );
            return saveVersion.data?.data;
        } catch (error: any) {
            console.log("Error in getting master information", error.response);
            enqueueSnackbar(error.response?.data?.message, {
                variant: "warning",
            });
        } finally {
            setLoader(false);
        }
    };

    const handleClosePopup = () => {
        setVersionPopup(false);
    };

    const openDeleteModal = (page: any) => {
        setSelectedPage(page);
        setIsDeleteModalOpen(true);
    };

    const closeDeleteModal = () => {
        setIsDeleteModalOpen(false);
        setSelectedPage(null);
    };

    const deleteTheObject = async () => {
        try {
            setLoader(true);
            const deleteRail = await axios.delete(
                `${process.env.BASE_URL}api/v1/cms/page/${selectedPage.id}?status=${selectedPage.publishStatus}`
            );
            if (deleteRail?.data?.success) {
                enqueueSnackbar(deleteRail?.data?.message, {
                    variant: "success",
                });
                getOtherPages();
                closeDeleteModal();
            } else {
                enqueueSnackbar(deleteRail?.data?.message, {
                    variant: "error",
                });
            }
        } catch (error: any) {
            enqueueSnackbar(error?.response?.data?.message, {
                variant: "error",
            });
            console.log("Error in getting master information", error);
        } finally {
            setLoader(false);
        }
    };

    const handleAddVersion = async (value: any) => {
        console.log(value);
        const payload = {
            versionName: value.versionName,
            canonicalName: value.canonicalName,
            template: value.template,
            publishStatus: "draft",
        };
        await createVersionAPI(payload);
    };

    const navigateToList = (id: string) => {
        navigate("/content-curation-list?pageName=" + id);
    };

    const navigateToPageDetails = (id: string) => {
        navigate("/content-curation-list?pageName=" + id);
    };

    const createVersionAPI = async (value: any) => {
        try {
            setLoader(true);
            const saveVersion = await axios.post(
                `${process.env.BASE_URL}api/v1/cms/page/versions`,
                value
            );
            if (saveVersion?.data?.success) {
                enqueueSnackbar(saveVersion?.data?.message, {
                    variant: "success",
                });
                navigateToList(saveVersion?.data?.data?.id);
                handleClosePopup();
            } else {
                enqueueSnackbar(saveVersion?.data?.message, {
                    variant: "error",
                });
            }
        } catch (error: any) {
            if (
                error.response?.data?.message &&
                error.response?.data?.message.includes("already exists")
            ) {
                createVersionPopupRef.current.setError(UNIQUE_NAME_ERROR);
            } else {
                console.log(
                    "Error in getting master information",
                    error.response
                );
                enqueueSnackbar(error.response?.data?.message, {
                    variant: "warning",
                });
            }
        } finally {
            setLoader(false);
        }
    };

    const handlePageConfiguration = (id: string) => {
        navigateToPageDetails(id);
    };

    const tableRowRender = () => {
        return (
            <TableRow>
                <TableCell>
                    <h4>Name</h4>
                </TableCell>
                <TableCell>
                    <h4>Page</h4>
                </TableCell>
                <TableCell>
                    <h4>Status</h4>
                </TableCell>
                <TableCell>
                    <h4>Date</h4>
                </TableCell>
                <TableCell align="center" width={100}>
                    <h4>Actions</h4>
                </TableCell>
            </TableRow>
        );
    };

    const handleChangePage = async (event: unknown, newPage: number) => {
        let skipKeyValue = "";
        let queue;
        if (newPage < page) {
            queue = popTwice();
            setskipKeyQueue([...queue]);
            skipKeyValue = queue[queue.length - 1] || "";
            console.log(skipKeyQueue);
        } else {
            skipKeyValue = skipKeyQueue[skipKeyQueue.length - 1];
        }
        setPage(newPage);
        const pages = await getPagesDraft(
            DRAFT_STATUS,
            rowsPerPage,
            skipKeyValue,
            queue
        );
        setDraftVersionList([...pages.list]);
        setTotalCount(pages.totalCount);
    };

    const handleChangeRowsPerPage = async (
        event: React.ChangeEvent<HTMLInputElement>
    ) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
        setskipKeyQueue([]);
        const pages = await getPagesDraft(DRAFT_STATUS, event.target.value);
        setDraftVersionList([...pages.list]);
        setTotalCount(pages.totalCount);
    };

    const pushKey = (key: any, queue: any = skipKeyQueue) => {
        const skipValue = [...queue, key];
        setskipKeyQueue(skipValue);
    };

    const popKey = (queue: any[] = skipKeyQueue) => {
        const skipValue = [...queue];
        const popValue = skipValue.pop();
        setskipKeyQueue(skipValue);
        return skipValue;
    };

    const popTwice = () => {
        const queue1 = popKey();
        const queue = popKey(queue1);
        return queue;
    };

    const refreshDraft = () => draftTableRef.current.getOtherPages();

    const refreshSchedule = () => scheduleTableRef.current.getOtherPages();

    const refreshTables = () => {
        refreshDraft();
        refreshSchedule();
    };

    return (
        <div className="config-heading">
            {/* List of all pages */}
            <div className="header-content-list">
                <div>
                    <h3>
                        Content Curation Versions{" "}
                        {loader && <CircularProgress size={30} />}
                    </h3>
                </div>
                <div className="d-flex j-center">
                    {hasPermission(PERMISSIONS.curation, ACTION.EDIT) && (
                        <Button
                            className="float-right mb-2"
                            onClick={() => setVersionPopup(true)}
                            variant="contained"
                        >
                            New version
                        </Button>
                    )}
                </div>
            </div>

            {/* Live Status - Version List */}
            <ContentCurationVersionTable
                status={LIVE}
                showActionButton={false}
            />

            {/* Scheduled Status - Version List */}
            <ContentCurationVersionTable
                ref={scheduleTableRef}
                status={SCHEDULED_STATUS}
                showActionButton={true}
                refreshTables={refreshTables}
            />

            {/* Draft Status - Version List */}
            <ContentCurationVersionTable
                ref={draftTableRef}
                status={DRAFT_STATUS}
                showActionButton={true}
            />

            {versionPopup && (
                <CreateContentVersionPopup
                    ref={createVersionPopupRef}
                    show={versionPopup}
                    handleCloseCB={handleClosePopup}
                    handleAddVersion={handleAddVersion}
                />
            )}

            <DeleteModal
                title="Delete Page?"
                isOpen={isDeleteModalOpen}
                handleClose={closeDeleteModal}
                handleConfirm={deleteTheObject}
            >
                <p>
                    Are you sure you want to delete {selectedPage?.versionName}?
                </p>
            </DeleteModal>
        </div>
    );
};

export default ContentCurationVersions;
